import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import {
  X, Minus, Maximize2, Minimize2, Paperclip, Bold, Italic,
  Underline as UnderlineIcon, List, Send,
  Trash2, FileIcon
} from 'lucide-react'
import { useMailStore } from '../../stores/mailStore'
import { useUIStore } from '../../stores/uiStore'
import { useAccountStore } from '../../stores/accountStore'
import { contacts, compose as composeApi } from '../../lib/api'
import type { ComposeState, Address } from '../../lib/types'
import { clsx } from 'clsx'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Recipient input with autocomplete ───────────────────────────────────────

interface RecipientInputProps {
  label: string
  value: Address[]
  onChange: (addresses: Address[]) => void
  placeholder?: string
}

function RecipientInput({ label, value, onChange, placeholder }: RecipientInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<Array<{ email: string; name: string }>>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) { setSuggestions([]); return }
    try {
      const results = await contacts.autocomplete(q)
      setSuggestions(results)
      setShowSuggestions(results.length > 0)
    } catch {
      // Mock suggestions in dev
      setSuggestions([
        { email: 'jordan@acme.com', name: 'Jordan Lee' },
        { email: 'sarah@acme.com', name: 'Sarah Chen' },
      ].filter((s) => s.email.includes(q) || s.name.toLowerCase().includes(q.toLowerCase())))
      setShowSuggestions(true)
    }
  }, [])

  const addAddress = useCallback((email: string, name = '') => {
    if (!email.includes('@')) return
    const addr = { email: email.trim(), name }
    if (!value.find((a) => a.address === addr.email)) {
      onChange([...value, { address: addr.email, name: addr.name }])
    }
    setInputValue('')
    setSuggestions([])
    setShowSuggestions(false)
  }, [value, onChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault()
      if (inputValue.trim()) addAddress(inputValue.trim())
    }
    if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => fetchSuggestions(inputValue), 200)
    return () => clearTimeout(timer)
  }, [inputValue, fetchSuggestions])

  return (
    <div className="recipient-input">
      <span className="recipient-input__label">{label}</span>
      <div className="recipient-input__field" onClick={() => inputRef.current?.focus()}>
        {value.map((addr) => (
          <span key={addr.address} className="recipient-chip">
            {addr.name || addr.address}
            <button
              className="recipient-chip__remove"
              onClick={(e) => { e.stopPropagation(); onChange(value.filter((a) => a.address !== addr.address)) }}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setTimeout(() => setShowSuggestions(false), 150)
            if (inputValue.trim()) addAddress(inputValue.trim())
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className="recipient-input__text"
          autoComplete="off"
        />
      </div>

      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            className="autocomplete-dropdown"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            {suggestions.map((s) => (
              <button
                key={s.email}
                className="autocomplete-item"
                onMouseDown={(e) => { e.preventDefault(); addAddress(s.email, s.name) }}
              >
                <span className="autocomplete-item__name">{s.name}</span>
                <span className="autocomplete-item__email">{s.email}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Editor toolbar ───────────────────────────────────────────────────────────

function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null
  return (
    <div className="editor-toolbar">
      <button
        className={clsx('btn-icon editor-toolbar__btn', { active: editor.isActive('bold') })}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run() }}
        title="Bold (⌘B)"
      >
        <Bold size={13} />
      </button>
      <button
        className={clsx('btn-icon editor-toolbar__btn', { active: editor.isActive('italic') })}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run() }}
        title="Italic (⌘I)"
      >
        <Italic size={13} />
      </button>
      <button
        className={clsx('btn-icon editor-toolbar__btn', { active: editor.isActive('underline') })}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run() }}
        title="Underline (⌘U)"
      >
        <UnderlineIcon size={13} />
      </button>
      <div className="editor-toolbar__sep" />
      <button
        className={clsx('btn-icon editor-toolbar__btn', { active: editor.isActive('bulletList') })}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run() }}
        title="Bullet list"
      >
        <List size={13} />
      </button>
    </div>
  )
}

// ─── Compose Window ───────────────────────────────────────────────────────────

interface ComposeWindowProps {
  compose: ComposeState
  index: number
  total: number
}

export function ComposeWindow({ compose, index, total }: ComposeWindowProps) {
  const { closeCompose, updateCompose, minimizeCompose } = useMailStore()
  const { addToast } = useUIStore()
  const [showCc, setShowCc] = useState(compose.cc.length > 0)
  const [showBcc, setShowBcc] = useState(compose.bcc ? compose.bcc.length > 0 : false)
  const [isSending, setIsSending] = useState(false)
  const [subject, setSubject] = useState(compose.subject)
  const [attachments, setAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Write your message…' }),
      Link.configure({ openOnClick: false }),
      Underline,
    ],
    content: compose.body_html || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      updateCompose(compose.id, { body_html: html })
      // Autosave after 2s of inactivity
      clearTimeout(autoSaveTimer.current)
      autoSaveTimer.current = setTimeout(() => {
        // TODO: call compose.saveDraft(...)
      }, 2000)
    },
  })

  useEffect(() => () => clearTimeout(autoSaveTimer.current), [])

  const handleSend = async () => {
    if (compose.to.length === 0) {
      addToast('Add at least one recipient', 'error')
      return
    }
    setIsSending(true)
    try {
      const bodyHtml = editor?.getHTML() ?? compose.body_html ?? ''
      // Strip HTML tags for plain-text version
      const bodyText = bodyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

      await composeApi.send({
        to: compose.to,
        cc: compose.cc,
        bcc: compose.bcc ?? [],
        subject: subject || '(no subject)',
        body_html: bodyHtml,
        body_text: bodyText,
        draft_id: compose.draft_id,
        attachments,
      })

      // Save a copy to the Sent folder via JMAP so it appears in Sent Items.
      // The internal SMTP relay doesn't do this automatically.
      try {
        const client = useAccountStore.getState().getActiveClient()
        const mailboxes = useAccountStore.getState().getActiveMailboxes()
        const sentMb = client?.findMailboxByRole(mailboxes, 'sent')
        const user = useMailStore.getState().user
        if (client && sentMb && user) {
          await client.request([
            ['Email/set', {
              accountId: client.accountId,
              create: {
                sent: {
                  mailboxIds: { [sentMb.id]: true },
                  keywords: { '$seen': true },
                  from: [{ name: user.name ?? '', email: user.email }],
                  to: compose.to.map(a => ({ name: a.name ?? '', email: a.address })),
                  cc: compose.cc?.map(a => ({ name: a.name ?? '', email: a.address })),
                  subject: subject || '(no subject)',
                  bodyValues: {
                    html: { value: bodyHtml, charset: 'utf-8' },
                    text: { value: bodyText, charset: 'utf-8' },
                  },
                  htmlBody: [{ partId: 'html', type: 'text/html' }],
                  textBody: [{ partId: 'text', type: 'text/plain' }],
                },
              },
            }, '0'],
          ])
        }
      } catch {
        // Non-fatal: email was already sent — just couldn't save to Sent folder
      }

      addToast('Message sent', 'success')
      closeCompose(compose.id)
    } catch (err: any) {
      addToast(err.message ?? 'Failed to send', 'error')
    } finally {
      setIsSending(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setAttachments(prev => [...prev, ...files])
    // Reset input so the same file can be re-attached if removed
    e.target.value = ''
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  // Position windows from right side, stacked
  const rightOffset = index * 16 + 16
  const bottomOffset = 0

  if (compose.is_minimized) {
    return (
      <motion.div
        className="compose-minimized"
        style={{ right: `${(index * 220) + 16}px` }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
      >
        <span className="compose-minimized__subject">
          {subject || 'New Message'}
        </span>
        <div className="compose-minimized__actions">
          <button className="btn-icon" onClick={() => minimizeCompose(compose.id)}>
            <Maximize2 size={12} />
          </button>
          <button className="btn-icon" onClick={() => closeCompose(compose.id)}>
            <X size={12} />
          </button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      className={clsx('compose-window', { 'compose-window--fullscreen': compose.is_fullscreen })}
      style={compose.is_fullscreen ? {} : {
        right: `${rightOffset}px`,
        bottom: `${bottomOffset}px`,
      }}
      initial={{ y: 20, opacity: 0, scale: 0.97 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: 20, opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Header */}
      <div className="compose-window__header">
        <span className="compose-window__title">
          {subject || 'New Message'}
        </span>
        <div className="compose-window__header-actions">
          <button className="btn-icon" onClick={() => minimizeCompose(compose.id)} title="Minimize">
            <Minus size={14} />
          </button>
          <button
            className="btn-icon"
            onClick={() => updateCompose(compose.id, { is_fullscreen: !compose.is_fullscreen })}
            title={compose.is_fullscreen ? 'Restore' : 'Expand'}
          >
            {compose.is_fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button className="btn-icon" onClick={() => closeCompose(compose.id)} title="Close">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Recipients */}
      <div className="compose-window__fields">
        <div className="compose-window__fields-left">
          <RecipientInput
            label="To"
            value={compose.to}
            onChange={(to) => updateCompose(compose.id, { to })}
            placeholder="Recipients"
          />
          <AnimatePresence>
            {showCc && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <RecipientInput
                  label="Cc"
                  value={compose.cc}
                  onChange={(cc) => updateCompose(compose.id, { cc })}
                />
              </motion.div>
            )}
            {showBcc && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <RecipientInput
                  label="Bcc"
                  value={compose.bcc || []}
                  onChange={(bcc) => updateCompose(compose.id, { bcc })}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="compose-window__fields-toggles">
          {!showCc && (
            <button className="btn-ghost btn-sm" onClick={() => setShowCc(true)}>Cc</button>
          )}
          {!showBcc && (
            <button className="btn-ghost btn-sm" onClick={() => setShowBcc(true)}>Bcc</button>
          )}
        </div>
      </div>

      {/* Subject */}
      <input
        className="compose-window__subject"
        placeholder="Subject"
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value)
          updateCompose(compose.id, { subject: e.target.value })
        }}
      />

      {/* Editor */}
      <div className="compose-window__body">
        <EditorContent editor={editor} className="tiptap-editor" />
      </div>

      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="compose-attachments">
          {attachments.map((file, i) => (
            <div key={`${file.name}-${i}`} className="compose-attachment-chip">
              <FileIcon size={11} />
              <span className="compose-attachment-name">{file.name}</span>
              <span className="compose-attachment-size">{formatBytes(file.size)}</span>
              <button
                className="compose-attachment-remove"
                onClick={() => removeAttachment(i)}
                title="Remove attachment"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Footer */}
      <div className="compose-window__footer">
        <div className="compose-window__footer-left">
          <motion.button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={isSending}
            whileTap={{ scale: 0.97 }}
          >
            <Send size={13} />
            {isSending ? 'Sending…' : 'Send'}
          </motion.button>
          <EditorToolbar editor={editor} />
          <button
            className={clsx('btn-icon', { 'btn-icon--active': attachments.length > 0 })}
            title="Attach files"
            onClick={() => fileInputRef.current?.click()}
            style={{ position: 'relative' }}
          >
            <Paperclip size={14} />
            {attachments.length > 0 && (
              <span className="compose-attach-badge">{attachments.length}</span>
            )}
          </button>
        </div>
        <button
          className="btn-icon"
          onClick={() => closeCompose(compose.id)}
          title="Discard draft"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </motion.div>
  )
}

// ─── Compose layer (renders all open windows) ─────────────────────────────────

export function ComposeLayer() {
  const { composeWindows } = useMailStore()

  return (
    <>
      <AnimatePresence>
        {composeWindows.map((compose, i) => (
          <ComposeWindow
            key={compose.id}
            compose={compose}
            index={i}
            total={composeWindows.length}
          />
        ))}
      </AnimatePresence>

      <style>{`
        .compose-window {
          position: fixed;
          bottom: 0;
          width: 560px;
          max-height: 80vh;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg) var(--radius-lg) 0 0;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-xl);
          z-index: 1000;
          overflow: hidden;
        }

        .compose-window--fullscreen {
          position: fixed;
          top: 5vh;
          left: 10vw;
          right: 10vw;
          bottom: 5vh;
          width: auto;
          max-height: none;
          border-radius: var(--radius-lg);
          z-index: 1100;
        }

        .compose-window__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px 10px;
          background: var(--bg-elevated);
          border-bottom: 1px solid var(--border);
          cursor: default;
          flex-shrink: 0;
        }

        .compose-window__title {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .compose-window__header-actions {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .compose-window__fields {
          display: flex;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
        }

        .compose-window__fields-left {
          flex: 1;
          min-width: 0;
        }

        .compose-window__fields-toggles {
          display: flex;
          flex-direction: column;
          padding: 6px 8px;
          gap: 2px;
          border-left: 1px solid var(--border);
        }

        .compose-window__subject {
          width: 100%;
          padding: 9px 14px;
          border: none;
          border-bottom: 1px solid var(--border);
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-ui);
          font-size: 13px;
          font-weight: 500;
          outline: none;
          flex-shrink: 0;
        }
        .compose-window__subject::placeholder {
          color: var(--text-tertiary);
        }

        .compose-window__body {
          flex: 1;
          overflow-y: auto;
          padding: 12px 14px;
          min-height: 120px;
        }

        .compose-window__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-top: 1px solid var(--border);
          background: var(--bg-elevated);
          flex-shrink: 0;
          gap: 8px;
        }

        .compose-window__footer-left {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* Minimized */
        .compose-minimized {
          position: fixed;
          bottom: 0;
          height: 36px;
          min-width: 200px;
          max-width: 220px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-strong);
          border-bottom: none;
          border-radius: var(--radius-md) var(--radius-md) 0 0;
          display: flex;
          align-items: center;
          padding: 0 8px 0 12px;
          gap: 8px;
          box-shadow: var(--shadow-md);
          z-index: 1000;
          cursor: pointer;
        }

        .compose-minimized__subject {
          flex: 1;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .compose-minimized__actions {
          display: flex;
          gap: 2px;
        }

        /* Recipient input */
        .recipient-input {
          display: flex;
          align-items: flex-start;
          gap: 0;
          border-bottom: 1px solid var(--border);
          position: relative;
        }

        .recipient-input__label {
          padding: 9px 0 9px 14px;
          font-size: 12px;
          color: var(--text-tertiary);
          flex-shrink: 0;
          width: 32px;
          line-height: 1.5;
        }

        .recipient-input__field {
          flex: 1;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
          padding: 6px 10px 6px 6px;
          min-height: 36px;
          cursor: text;
        }

        .recipient-input__text {
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-ui);
          font-size: 13px;
          outline: none;
          flex: 1;
          min-width: 120px;
          padding: 2px 0;
        }

        .recipient-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px 2px 8px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-full);
          font-size: 12px;
          color: var(--text-primary);
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .recipient-chip__remove {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-tertiary);
          padding: 0;
          border-radius: 50%;
        }
        .recipient-chip__remove:hover { color: var(--text-primary); }

        .autocomplete-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-top: none;
          border-radius: 0 0 var(--radius-md) var(--radius-md);
          z-index: 1200;
          box-shadow: var(--shadow-md);
          max-height: 200px;
          overflow-y: auto;
        }

        .autocomplete-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 9px 14px;
          border: none;
          background: transparent;
          cursor: pointer;
          text-align: left;
          transition: background var(--duration-fast);
        }
        .autocomplete-item:hover { background: var(--bg-hover); }

        .autocomplete-item__name {
          font-size: 13px;
          color: var(--text-primary);
          font-weight: 500;
        }

        .autocomplete-item__email {
          font-size: 12px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
        }

        /* Editor toolbar */
        .editor-toolbar {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .editor-toolbar__btn {
          padding: 4px 6px;
          border-radius: 4px;
        }

        .editor-toolbar__btn.active {
          background: var(--brand-subtle);
          color: var(--brand);
        }

        .editor-toolbar__sep {
          width: 1px;
          height: 16px;
          background: var(--border);
          margin: 0 4px;
        }

        /* Attachment list */
        .compose-attachments {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 6px 14px 2px;
          border-top: 1px solid var(--border);
          background: var(--bg-root);
        }

        .compose-attachment-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 6px 3px 8px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-md);
          font-size: 11px;
          color: var(--text-secondary);
          max-width: 220px;
        }

        .compose-attachment-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          color: var(--text-primary);
        }

        .compose-attachment-size {
          color: var(--text-tertiary);
          font-size: 10px;
          flex-shrink: 0;
        }

        .compose-attachment-remove {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-tertiary);
          padding: 0;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .compose-attachment-remove:hover { color: var(--text-primary); }

        /* Paperclip active state + badge */
        .btn-icon--active { color: var(--brand); }

        .compose-attach-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 14px;
          height: 14px;
          background: var(--brand);
          color: #fff;
          font-size: 9px;
          font-weight: 600;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }
      `}</style>
    </>
  )
}
